<template>
  <v-row>
    <v-col cols="12" sm="8">
      <v-card v-if="isLoading">
        <v-card-text>
          <div class="text-center ma-10">
            <v-progress-circular indeterminate></v-progress-circular>
          </div>
        </v-card-text>
      </v-card>
      <v-card v-else :class="{ 'pt-3': logoAsImage }">
        <template v-if="video">
          <video controls style="width:100%">
            <source :src="video">
            Your browser does not support the video tag.
          </video>
        </template>
        <template v-else>
          <v-img v-if="logoAsImage" :src="tenant.logo.full_hd" :aspect-ratio="16 / 10" contain />
          <v-img v-else :src="getPrimaryImage(news).full_hd"></v-img>
        </template>
        <v-card-title>
          <h3>{{ news.title }}</h3>
        </v-card-title>

        <v-card-text>
          <div v-html="markdownToHtml(news.text)"></div>
          <template v-if="attachments.length">
            <div class="">
              <v-list>
                <v-list-item v-for="attachment in attachments" :key="`attachment-${attachment.id}`"
                  @click="openAttachment(attachment)">
                  <v-list-item-icon>
                    <v-icon>{{ icons.mdiAttachment }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    {{ attachment.name }}
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </div>
          </template>
          <div v-if="tenant.settings.tags_enabled && news.tags && news.tags.length > 0">
            <v-chip v-for="tag in news.tags" :key="tag.id" class="mr-1 my-2" color="primary" small>
              {{ tag.title }}
            </v-chip>
          </div>
          <div>
            <v-row class="mt-1">
              <v-col>
                <v-chip small>
                  {{ getDateTime(news.release_date) }}
                </v-chip>
              </v-col>
              <v-col>
                <div class="float-right">
                  <v-chip small>
                    {{ news.group_name }}
                  </v-chip>
                </div>
              </v-col>
            </v-row>
          </div>
        </v-card-text>
      </v-card>
      <br />
      <template v-if="news.poll && poll && !pollExpired">
        <poll-form v-if="poll" :poll="poll" :answered="pollAnswered" :isEmbedded="true"
          @submitted="pollAnswered = true">
        </poll-form>
      </template>
      <v-alert v-else-if="pollExpired" color="warning" text>
        {{ $t("Vastausaika on päättynyt") }}
      </v-alert>
    </v-col>
    <v-col sm="4">
      <v-card>
        <v-card-title>{{ $t("Uusimmat") }}</v-card-title>
        <newslist :news="newsList"></newslist>
      </v-card>
    </v-col>
    <v-dialog v-model="isDownloading" hide-overlay persistent width="240">
      <v-card color="primary" dark>
        <v-card-text class="pt-2">
          {{ $t("Ladataan...") }}

          <v-progress-linear indeterminate color="white" class="mb-0 mt-2"></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import { computed, onMounted, ref, watch } from "@vue/composition-api";
import { useTranslations } from "@/@core/libs/i18n";
import api from "@/api";
import useAuth from "@/useAuth";
import { getDateTime } from "@/@core/utils/datetime_helpers";
import useMarkdown from "@/@core/utils/useMarkdown";
import newslist from "@/components/news/newslist.vue";
import { mdiAttachment } from "@mdi/js";
import download from "downloadjs";
import pollForm from "@/components/polls/poll_form.vue";
import useNewsImage from "@/@core/utils/useNewsImage";


export default {
  props: ["id"],
  components: {
    newslist,
    pollForm,
  },
  setup(props) {
    const { t } = useTranslations();
    const { tenant, user } = useAuth();
    const { getPrimaryImage } = useNewsImage();
    const news = ref({});
    const newsList = ref([]);
    const poll = ref(null);
    const pollAnswered = ref(false);
    const pollExpired = ref(false);
    const isDownloading = ref(false);
    const isLoading = ref(true);

    const { markdownToHtml } = useMarkdown();

    const fetchNews = async (id) => {
      isLoading.value = true;
      const response = await api.getNews(id);

      if (response.ok) {
        if (response.data.poll && !user.value.read_only) {
          const pollResponse = await api.getPoll(response.data.poll);
          if (pollResponse.ok) {
            const pollAnswerResponse = await api.getUserPollAnswer(
              response.data.poll
            );

            poll.value = pollResponse.data;
            pollAnswered.value = pollAnswerResponse.status !== 404;
          } else {
            pollExpired.value = true;
          }
        }

        news.value = response.data;
      } else {
        alert(JSON.stringify(response.error));
      }
      isLoading.value = false;
    };

    const fetchNewsList = async () => {
      const response = await api.getNewsList(1, 5);

      if (response.ok) {
        newsList.value = response.data.results;
      } else {
        alert(response.error);
      }
    };

    watch(
      () => props.id,
      (newId, prevId) => {
        news.value = {};
        poll.value = {};
        pollAnswered.value = false;

        fetchNews(newId);
      }
    );

    onMounted(() => {
      if (props.id) {
        fetchNews(props.id);
      }
      fetchNewsList();
    });

    const attachments = computed(() => {
      if (news.value.attachments) {
        return news.value.attachments.filter((a) => a.attachment_type == 1);
      }
      return [];
    });

    const video = computed(() => {
      if (news.value.attachments) {
        const videoAttachment = news.value.attachments.find(
          (a) => a.attachment_type == 2
        );
        if (videoAttachment && videoAttachment.url) {
          return videoAttachment.url;
        }
      }
      return null;
    });

    // const image = computed(() => {
    //   if (news.value.image) {
    //     if (Object.keys(news.value.image).length) {
    //       return news.value.image.full_hd;
    //     }
    //     return tenant.value.logo.full_hd;
    //   }
    //   return null;
    // });

    const logoAsImage = computed(() => {
      return getPrimaryImage(news.value).thumbnail == tenant.value.logo.thumbnail;
    });

    // const logoAsImage = computed(() => {
    //   if (Object.keys(news.value).length) {
    //     if (Object.keys(news.value.image).length) {
    //       return false;
    //     }
    //   }
    //   return true;
    // });

    const openAttachment = async (attachment) => {
      isDownloading.value = true;
      const response = await api.downloadNewsAttachment(
        news.value.id,
        attachment.id
      );
      isDownloading.value = false;

      if (response.ok) {
        download(response.data, attachment.name);
      } else {
        alert(response.error);
      }
    };

    return {
      news,
      newsList,
      attachments,
      getPrimaryImage,
      video,
      logoAsImage,
      tenant,
      poll,
      pollAnswered,
      pollExpired,
      openAttachment,
      isLoading,
      isDownloading,
      t,
      markdownToHtml,
      getDateTime,
      icons: {
        mdiAttachment,
      },
    };
  },
};
</script>

<style></style>